import * as React from "react";
import { useIsMobile } from "@/hooks/useIsMobile";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "./drawer";

interface ModalContextValue {
  isMobile: boolean;
}

const ModalContext = React.createContext<ModalContextValue>({
  isMobile: false,
});

function useModalContext() {
  return React.useContext(ModalContext);
}

interface ModalProps {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  shouldScaleBackground?: boolean;
  snapPoints?: (number | string)[];
  activeSnapPoint?: number | string | null;
  onActiveSnapPointChange?: (snapPoint: number | string | null) => void;
  dismissible?: boolean;
}

function Modal({ children, ...props }: ModalProps) {
  const isMobile = useIsMobile();
  const {
    shouldScaleBackground,
    snapPoints,
    activeSnapPoint,
    onActiveSnapPointChange,
    dismissible,
    ...dialogProps
  } = props;

  if (isMobile) {
    return (
      <ModalContext.Provider value={{ isMobile }}>
        <Drawer {...props}>{children}</Drawer>
      </ModalContext.Provider>
    );
  }

  return (
    <ModalContext.Provider value={{ isMobile }}>
      <Dialog {...dialogProps}>{children}</Dialog>
    </ModalContext.Provider>
  );
}

function ModalTrigger({
  ...props
}: React.ComponentProps<typeof DialogTrigger>) {
  const { isMobile } = useModalContext();
  const Component = isMobile ? DrawerTrigger : DialogTrigger;
  return <Component {...props} />;
}

function ModalClose({ ...props }: React.ComponentProps<typeof DialogClose>) {
  const { isMobile } = useModalContext();
  const Component = isMobile ? DrawerClose : DialogClose;
  return <Component {...props} />;
}

function ModalContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof DialogContent>) {
  const { isMobile } = useModalContext();

  if (isMobile) {
    return (
      <DrawerContent className={className} {...props}>
        {children}
      </DrawerContent>
    );
  }

  return (
    <DialogContent className={className} showCloseButton={false} {...props}>
      {children}
    </DialogContent>
  );
}

function ModalHeader({ ...props }: React.ComponentProps<typeof DialogHeader>) {
  const { isMobile } = useModalContext();
  const Component = isMobile ? DrawerHeader : DialogHeader;
  return <Component {...props} />;
}

function ModalFooter({ ...props }: React.ComponentProps<typeof DialogFooter>) {
  const { isMobile } = useModalContext();
  const Component = isMobile ? DrawerFooter : DialogFooter;
  return <Component {...props} />;
}

function ModalTitle({ ...props }: React.ComponentProps<typeof DialogTitle>) {
  const { isMobile } = useModalContext();
  const Component = isMobile ? DrawerTitle : DialogTitle;
  return <Component {...props} />;
}

function ModalDescription({
  ...props
}: React.ComponentProps<typeof DialogDescription>) {
  const { isMobile } = useModalContext();
  const Component = isMobile ? DrawerDescription : DialogDescription;
  return <Component {...props} />;
}

export {
  Modal,
  ModalClose,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  ModalTrigger,
};
